import { useState } from 'react'

const TYPES = [
  { id: 'bug',     label: '🐛 Bug report',      hint: 'Something broken or not working as expected' },
  { id: 'feature', label: '✨ Feature idea',     hint: 'Something you wish Flow could do' },
  { id: 'other',   label: '💬 General feedback', hint: 'Anything else on your mind' },
]

export default function FeedbackModal({ onClose }) {
  const [type, setType]       = useState('bug')
  const [message, setMessage] = useState('')
  const [state, setState]     = useState('idle') // idle | sending | done | error
  const [errMsg, setErrMsg]   = useState('')

  const submit = async () => {
    if (message.trim().length < 5) return
    setState('sending')
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message, page: window.location.pathname }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to send')
      setState('done')
    } catch (e) {
      setErrMsg(e.message)
      setState('error')
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(2,6,10,0.72)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '0 0 env(safe-area-inset-bottom,0)',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          background: 'linear-gradient(180deg, rgba(6,18,10,0.98) 0%, rgba(3,10,6,0.99) 100%)',
          border: '1.5px solid rgba(34,197,94,0.14)',
          borderBottom: 'none',
          borderRadius: '22px 22px 0 0',
          padding: '22px 20px 32px',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.6)',
          animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: 'rgba(255,255,255,0.12)',
          margin: '0 auto 20px',
        }} />

        {state === 'done' ? (
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🙏</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#c8e6cb', marginBottom: 8 }}>
              Thanks for the feedback
            </div>
            <p style={{ fontSize: 13, color: 'rgba(140,200,150,0.65)', lineHeight: 1.6, margin: '0 0 24px' }}>
              We read everything and use it to make Flow better.
            </p>
            <button onClick={onClose} style={{
              background: 'linear-gradient(135deg,rgba(34,197,94,0.2),rgba(20,120,50,0.15))',
              border: '1.5px solid rgba(34,197,94,0.3)',
              color: '#86efac', borderRadius: 12, padding: '12px 32px',
              fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
            }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#c8e6cb', marginBottom: 4 }}>
              Send feedback
            </div>
            <p style={{ fontSize: 13, color: 'rgba(140,200,150,0.5)', marginBottom: 20, lineHeight: 1.5 }}>
              Report a bug or share an idea — we'd love to hear it.
            </p>

            {/* Type picker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {TYPES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: type === t.id ? 'rgba(34,197,94,0.1)' : 'rgba(4,14,7,0.8)',
                    border: `1.5px solid ${type === t.id ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: 13, padding: '11px 14px', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left',
                    WebkitTapHighlightColor: 'transparent', transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${type === t.id ? '#22c55e' : 'rgba(255,255,255,0.15)'}`,
                    background: type === t.id ? '#22c55e' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {type === t.id && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: type === t.id ? '#c8e6cb' : 'rgba(180,215,235,0.55)' }}>
                      {t.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(140,200,150,0.4)', marginTop: 1 }}>{t.hint}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Message */}
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind…"
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                background: 'rgba(4,14,7,0.85)',
                border: '1.5px solid rgba(34,197,94,0.15)',
                borderRadius: 13, padding: '12px 14px',
                color: '#c8e6cb', fontSize: 13, lineHeight: 1.6,
                fontFamily: 'inherit', resize: 'none', outline: 'none',
                caretColor: '#22c55e',
              }}
              onFocus={e => e.target.style.borderColor = 'rgba(34,197,94,0.35)'}
              onBlur={e => e.target.style.borderColor = 'rgba(34,197,94,0.15)'}
            />
            <div style={{
              fontSize: 10, color: 'rgba(140,200,150,0.3)',
              textAlign: 'right', marginTop: 4, marginBottom: 18,
            }}>
              {message.length} / 2000
            </div>

            {state === 'error' && (
              <div style={{
                background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)',
                borderRadius: 10, padding: '10px 14px', color: '#fca5a5',
                fontSize: 12, marginBottom: 14,
              }}>
                {errMsg || 'Something went wrong — please try again.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{
                flex: 1, background: 'rgba(4,14,7,0.7)',
                border: '1px solid rgba(255,255,255,0.07)',
                color: 'rgba(180,215,235,0.5)', borderRadius: 12, padding: '13px 0',
                fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
              }}>Cancel</button>
              <button
                onClick={submit}
                disabled={message.trim().length < 5 || state === 'sending'}
                style={{
                  flex: 2,
                  background: message.trim().length >= 5
                    ? 'linear-gradient(135deg,rgba(34,197,94,0.28),rgba(20,120,50,0.2))'
                    : 'rgba(4,14,7,0.6)',
                  border: `1.5px solid ${message.trim().length >= 5 ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.05)'}`,
                  color: message.trim().length >= 5 ? '#86efac' : 'rgba(140,200,150,0.25)',
                  borderRadius: 12, padding: '13px 0',
                  fontSize: 14, fontWeight: 700, cursor: message.trim().length >= 5 ? 'pointer' : 'default',
                  fontFamily: 'inherit', transition: 'all 0.18s',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {state === 'sending' ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  )
}
